/*
 * <price-per-quantity> — met à jour le prix affiché (.price-item--regular /
 * .price-item--sale / .price-item--last) sur la page produit en fonction de la
 * quantité choisie dans .product-form__quantity.
 *
 * Le prix unitaire (en centimes) et le format monétaire de la boutique sont
 * lus sur l'élément #price-{{ section.id }} via les data-attributs posés par
 * snippets/price.liquid :
 *   - data-unit-price          : prix unitaire courant, en centimes
 *   - data-unit-compare-price  : prix comparé unitaire, en centimes (optionnel)
 *   - data-money-format        : shop.money_format
 */
if (!customElements.get('price-per-quantity')) {
  class PricePerQuantity extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info') || document;
      this.onQuantityChange = this.onQuantityChange.bind(this);

      // Changement direct dans l'input quantité (clavier + boutons +/-)
      this.productInfo.addEventListener('change', (event) => {
        if (event.target && event.target.classList.contains('quantity__input')) {
          this.onQuantityChange();
        }
      });

      // Événements pub/sub du thème (changement de variante, règles de quantité)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.variantSub = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          // Le bloc prix vient d'être ré-injecté avec le nouveau prix unitaire :
          // on ré-applique la quantité au tick suivant.
          requestAnimationFrame(() => this.onQuantityChange());
        });
        this.quantitySub = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
      }

      this.onQuantityChange();
    }

    disconnectedCallback() {
      if (this.variantSub) this.variantSub();
      if (this.quantitySub) this.quantitySub();
    }

    get priceContainer() {
      // Toujours re-sélectionner : le contenu est remplacé lors d'un changement de variante.
      return this.querySelector('[data-unit-price]') || this.querySelector('.price');
    }

    get quantityInput() {
      return this.productInfo.querySelector('.product-form__quantity .quantity__input');
    }

    onQuantityChange() {
      const container = this.priceContainer;
      const input = this.quantityInput;
      if (!container || !input) return;

      const quantity = Math.max(1, parseInt(input.value, 10) || 1);
      const unitPrice = parseInt(container.dataset.unitPrice, 10);
      if (Number.isNaN(unitPrice)) return;

      const format = container.dataset.moneyFormat || '{{amount}}';
      const comparePrice = parseInt(container.dataset.unitComparePrice, 10);

      this.setText(container, '.price-item--regular', this.formatMoney(unitPrice * quantity, format));
      this.setText(container, '.price-item--sale', this.formatMoney(unitPrice * quantity, format));

      // .price-item--last est aussi utilisé pour le prix unitaire (unit-price) :
      // on ne touche qu'à celui du bloc sale.
      const saleLast = container.querySelector('.price__sale .price-item--last');
      if (saleLast) saleLast.textContent = this.formatMoney(unitPrice * quantity, format);

      if (!Number.isNaN(comparePrice)) {
        const compareEl = container.querySelector('.price__sale s.price-item--regular');
        if (compareEl) compareEl.textContent = this.formatMoney(comparePrice * quantity, format);
      }
    }

    setText(scope, selector, value) {
      scope.querySelectorAll(selector).forEach((el) => {
        // Ne pas écraser le <s> (prix comparé) traité à part.
        if (el.tagName === 'S') return;
        // Ne pas toucher au prix unitaire "à l'unité"
        if (el.closest('.unit-price')) return;
        el.textContent = value;
      });
    }

    /* Formatage monétaire minimal compatible shop.money_format */
    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (Number.isNaN(number) || number == null) return 0;
        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsPart = parts[1] ? decimal + parts[1] : '';
        return dollars + centsPart;
      };

      let value = '';
      const match = format.match(placeholderRegex);
      const placeholder = match ? match[1] : 'amount';

      switch (placeholder) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ', '');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value);
    }
  }

  customElements.define('price-per-quantity', PricePerQuantity);
}
